'use client';

import type { FC, ImgHTMLAttributes } from 'react';
import { useCallback, useState } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../utils/cn';
import { getAvatarGradient, getAvatarInitials } from './avatarFallback';
import { forgetImageLoaded, isImageLoaded, markImageLoaded } from './loadedImages';

const avatarVariants = cva(
  'inline-flex items-center justify-center rounded-full overflow-hidden shrink-0 select-none',
  {
    variants: {
      size: {
        s: 'size-8 text-[13px]',
        m: 'size-12 text-[18px]',
        l: 'size-16 text-[24px]',
        xl: 'size-24 text-[36px]',
      },
    },
    defaultVariants: {
      size: 'm',
    },
  },
);

interface AvatarProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'size'> {
  size?: 's' | 'm' | 'l' | 'xl';
  fallbackKey?: string | number;
  firstName?: string | null;
  lastName?: string | null;
  fallback?: string;
}

export const Avatar: FC<AvatarProps> = ({
  size = 'm',
  fallback,
  fallbackKey,
  firstName,
  lastName,
  className,
  src,
  alt,
  ...props
}) => {
  const [hasError, setHasError] = useState(false);
  const cached = isImageLoaded(typeof src === 'string' ? src : undefined);
  const baseClasses = cn(avatarVariants({ size }), className);

  const captureNode = useCallback(
    (node: HTMLImageElement | null) => {
      if (typeof src !== 'string') return;
      if (node?.complete && node.naturalWidth > 0) markImageLoaded(src);
      else if (node) forgetImageLoaded(src);
    },
    [src],
  );

  if (!src || hasError) {
    const initials = getAvatarInitials(firstName, lastName);
    const key = fallbackKey ?? (`${firstName ?? ''}${lastName ?? ''}` || 'fallback');
    return (
      <div
        className={cn(baseClasses, 'text-white font-semibold')}
        style={{ background: getAvatarGradient(key) }}
      >
        {initials || fallback || '👤'}
      </div>
    );
  }

  return (
    <img
      ref={captureNode}
      className={baseClasses}
      src={src}
      alt={alt || 'Avatar'}
      loading={cached ? 'eager' : 'lazy'}
      decoding={cached ? 'sync' : 'async'}
      onLoad={() => markImageLoaded(typeof src === 'string' ? src : undefined)}
      onError={() => {
        forgetImageLoaded(typeof src === 'string' ? src : undefined);
        setHasError(true);
      }}
      {...props}
    />
  );
};
